// File hashing for the Hash Verifier / chain-of-custody tool.
//
// SHA-1/256/512 use the browser's native Web Crypto API. MD5 isn't offered by
// Web Crypto, so it falls back to the pure-JS implementation in md5.js —
// offered for interoperability with hash values already on record elsewhere
// (a lot of legacy forensic tooling defaults to MD5+SHA-1), not as a security
// recommendation.

import { md5 } from './md5';

export const ALGORITHMS = [
  { id: 'sha256', label: 'SHA-256', recommended: true },
  { id: 'sha1', label: 'SHA-1' },
  { id: 'md5', label: 'MD5' },
  { id: 'sha512', label: 'SHA-512' },
];

const WEB_CRYPTO_NAME = { sha1: 'SHA-1', sha256: 'SHA-256', sha512: 'SHA-512' };

export const DEFAULT_ALGORITHMS = ['sha256'];

function bufToHex(buf) {
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * @param {Uint8Array} bytes
 * @param {string} algorithmId one of ALGORITHMS' ids
 * @returns {Promise<string>} lowercase hex digest
 */
export async function computeHash(bytes, algorithmId) {
  if (algorithmId === 'md5') return md5(bytes);

  const webCryptoName = WEB_CRYPTO_NAME[algorithmId];
  if (!webCryptoName) throw new Error(`Unknown hash algorithm: ${algorithmId}`);

  const digest = await crypto.subtle.digest(webCryptoName, bytes);
  return bufToHex(digest);
}

/**
 * @param {Uint8Array} bytes
 * @param {string[]} algorithmIds
 * @param {(fraction:number)=>void} [onProgress]
 * @returns {Promise<Record<string,string>>} algorithmId → hex digest
 */
export async function computeHashes(bytes, algorithmIds, onProgress) {
  const results = {};
  for (let i = 0; i < algorithmIds.length; i++) {
    results[algorithmIds[i]] = await computeHash(bytes, algorithmIds[i]);
    onProgress?.((i + 1) / algorithmIds.length);
  }
  return results;
}

/** Strip whitespace, an optional "0x" prefix, and any non-hex noise, then lowercase. */
export function normalizeHash(s) {
  return String(s || '').trim().toLowerCase().replace(/^0x/, '').replace(/[^0-9a-f]/g, '');
}

/** Compares two hash strings ignoring case, whitespace, and formatting. */
export function hashesMatch(a, b) {
  const na = normalizeHash(a);
  const nb = normalizeHash(b);
  return !!na && !!nb && na === nb;
}

/** Which algorithm a hash string's length matches, if any — helps a pasted
 * "expected hash" guess what it's supposed to be without the user saying so. */
export function algorithmForHashLength(hash) {
  const len = normalizeHash(hash).length;
  if (len === 32) return 'md5';
  if (len === 40) return 'sha1';
  if (len === 64) return 'sha256';
  if (len === 128) return 'sha512';
  return null;
}
